// ---------------------------------------------------------------------------
// Guide progress: normalising what the server stores, and writing it back.
//
// Storage is the existing per-(user, slug) UserOnboarding row, patched through
// /api/v1/accounts/user/onboarding/progress/. One slug per guide id, plus the
// legacy 'admin-getting-started' slug which now holds only the dashboard
// checklist state (which tasks are ticked, whether the card was dismissed).
// ---------------------------------------------------------------------------

import axios from '../utils/axios';

export const ADMIN_TOUR_SLUG = 'admin-getting-started';

export const GUIDE_STATUS = {
  notStarted: 'not_started',
  inProgress: 'in_progress',
  paused: 'paused',
  completed: 'completed',
  skipped: 'skipped',
};

export function normalizeProgress(raw) {
  const p = raw || {};
  return {
    status: p.status || GUIDE_STATUS.notStarted,
    current_step: Number.isInteger(p.current_step) ? p.current_step : 0,
    completed_steps: Array.isArray(p.completed_steps) ? p.completed_steps : [],
    completed_tasks: Array.isArray(p.completed_tasks) ? p.completed_tasks : [],
    dismissed: !!p.dismissed,
    // `dismissed` is really a tri-state and this is the third value. Absent
    // means nobody has ever had an opinion, true means hidden, and an explicit
    // false means the user went and asked for the card back. That last one is
    // what lets an existing customer turn the checklist on for themselves: the
    // cohort rule decides who gets it PUSHED, and this decides who pulled it.
    // Without the distinction, "restored" would be indistinguishable from
    // "never touched" and the request would be silently ignored for exactly the
    // users who bothered to make it.
    restored: p.dismissed === false,
  };
}

export function isGuideDone(progress) {
  const s = normalizeProgress(progress).status;
  return s === GUIDE_STATUS.completed || s === GUIDE_STATUS.skipped;
}

// The endpoint returns the user's full progress map on every write. Whoever
// owns the auth user registers here once, so the engine can keep
// user.onboarding_progress fresh without threading a callback through every
// layer between the provider and the button that moved the step.
let syncHandler = null;

export function setProgressSyncHandler(fn) {
  syncHandler = fn;
  return () => { if (syncHandler === fn) syncHandler = null; };
}

// One in-flight write per slug. Step transitions can fire a few frames apart
// (click -> route change -> target found) and we do not need three round trips
// to record that the user is on step 4.
const pending = new Map(); // slug -> { patch, timer }
const FLUSH_MS = 700;

function flush(slug, onResult) {
  const entry = pending.get(slug);
  if (!entry) return;
  pending.delete(slug);
  axios
    .patch('/api/v1/accounts/user/onboarding/progress/', { slug, ...entry.patch })
    .then((r) => {
      const map = r?.data?.onboarding_progress;
      if (!map) return;
      if (onResult) onResult(map);
      else if (syncHandler) syncHandler(map);
    })
    .catch((e) => console.error(`[coach] failed to persist progress for "${slug}"`, e));
}

/**
 * Queue a progress patch. Later keys win, except completed_steps and
 * completed_tasks which accumulate (the server merges them too, but coalescing
 * here keeps a burst of steps in a single request).
 */
export function persistProgress(slug, patch, onResult) {
  if (!slug) return;
  const entry = pending.get(slug) || { patch: {}, timer: null };

  const merged = { ...entry.patch, ...patch };
  ['completed_steps', 'completed_tasks'].forEach((key) => {
    if (entry.patch[key] || patch[key]) {
      merged[key] = [...new Set([...(entry.patch[key] || []), ...(patch[key] || [])])];
    }
  });
  entry.patch = merged;

  if (entry.timer) clearTimeout(entry.timer);
  entry.timer = setTimeout(() => flush(slug, onResult), FLUSH_MS);
  pending.set(slug, entry);
}

/** Write immediately, bypassing the debounce. Use when the guide ends. */
export function persistProgressNow(slug, patch, onResult) {
  if (!slug) return;
  const entry = pending.get(slug);
  if (entry?.timer) clearTimeout(entry.timer);
  const merged = { ...(entry?.patch || {}), ...patch };
  pending.set(slug, { patch: merged, timer: null });
  flush(slug, onResult);
}
